import { c as _c } from "react/compiler-runtime";
import { ArrowLeft, MessagesSquare } from "lucide-react";
import { GlassEmptyState, GradientDivider } from "@/shared/ui/glass";
import { resolveAreaWithPage } from "@/entities/feature-announcement-feedback";
import type { useReplyAttachments } from "@/entities/feature-announcement-feedback";
import type { MyFeedbackThread } from "@/presentation/rpc/feature-announcements-rpc";
import { StatusBadge } from "./status-badge";
import { FeedbackThreadView } from "./feedback-thread-view";
import { FeedbackReplyBox } from "./feedback-reply-box";
type ThreadFeedback = MyFeedbackThread["feedback"];
type ThreadMessage = MyFeedbackThread["messages"][number];
export interface ThreadState {
  feedback: ThreadFeedback | null;
  messages: readonly ThreadMessage[];
  isLoading: boolean;
  error: unknown;
}

/**
 * The detail pane (2/3 on the page, full column on the widget): status progress,
 * the conversation, and the reply box.
 * Purely presentational — selection + reply state are owned by the central hook.
 */
export function FeedbackTicketDetail(t0) {
  const $ = _c(34);
  const {
    hasSelection,
    thread,
    replyDraft,
    setReplyDraft,
    onSend,
    isReplying,
    replyError,
    replyAttachments,
    onBack,
    hideHeader: t1
  } = t0;
  const hideHeader = t1 === undefined ? false : t1;
  if (!hasSelection) {
    let t2;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <GlassEmptyState icon={MessagesSquare} title="Select a ticket" description="Pick a feedback item on the left to see its status and conversation with the Cova team." />;
      $[0] = t2;
    } else {
      t2 = $[0];
    }
    return t2;
  }
  if (thread.isLoading && !thread.feedback) {
    let t2;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <p className="px-4 py-6 text-sm text-gray-400">Loading conversation...</p>;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    return t2;
  }
  const feedback = thread.feedback;
  const unavailable = Boolean(thread.error) || !feedback;
  if (unavailable || !feedback) {
    let t2;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <GlassEmptyState icon={MessagesSquare} title="Conversation unavailable" description="This conversation is no longer available. It may have been removed." />;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  let t2;
  if ($[3] !== feedback.featureKey || $[4] !== feedback.originFeatureKey) {
    t2 = resolveAreaWithPage(feedback.featureKey, feedback.originFeatureKey);
    $[3] = feedback.featureKey;
    $[4] = feedback.originFeatureKey;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const areaText = t2;
  let t3;
  if ($[6] !== onBack) {
    t3 = onBack && <button type="button" onClick={onBack} className="mb-2 inline-flex w-fit items-center gap-1 text-xs font-medium text-gray-500 transition-colors hover:text-[#091E42]"><ArrowLeft className="h-3.5 w-3.5" />Back</button>;
    $[6] = onBack;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== areaText || $[9] !== feedback.status || $[10] !== hideHeader) {
    t4 = !hideHeader && <><div className="mb-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-1"><h2 className="text-sm font-semibold text-[#091E42]">{areaText}</h2><StatusBadge status={feedback.status} /></div><GradientDivider /></>;
    $[8] = areaText;
    $[9] = feedback.status;
    $[10] = hideHeader;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== feedback || $[13] !== thread.messages) {
    t5 = <div className="min-h-0 flex-1 overflow-y-auto py-3"><FeedbackThreadView feedback={feedback} messages={thread.messages} /></div>;
    $[12] = feedback;
    $[13] = thread.messages;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <GradientDivider />;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== feedback.featureKey || $[17] !== feedback.isArchived || $[18] !== isReplying || $[19] !== onSend || $[20] !== replyAttachments?.addFiles || $[21] !== replyAttachments?.attachmentError || $[22] !== replyAttachments?.attachments || $[23] !== replyAttachments?.removeAttachment || $[24] !== replyDraft || $[25] !== replyError || $[26] !== setReplyDraft || $[27] !== thread.messages) {
    t7 = <div className="pt-3">{feedback.isArchived ? <p className="rounded-lg bg-gray-50 px-3 py-2 text-center text-xs leading-snug text-gray-500">This feedback has been archived by the Cova team, so you can no longer reply here.</p> : <FeedbackReplyBox value={replyDraft} onChange={setReplyDraft} onSend={() => onSend(feedback.featureKey)} isReplying={isReplying} error={replyError} showExpectation={thread.messages.length === 0} pendingAttachments={replyAttachments?.attachments} onAttachFiles={replyAttachments?.addFiles} onRemoveAttachment={replyAttachments?.removeAttachment} attachmentError={replyAttachments?.attachmentError} />}</div>;
    $[16] = feedback.featureKey;
    $[17] = feedback.isArchived;
    $[18] = isReplying;
    $[19] = onSend;
    $[20] = replyAttachments?.addFiles;
    $[21] = replyAttachments?.attachmentError;
    $[22] = replyAttachments?.attachments;
    $[23] = replyAttachments?.removeAttachment;
    $[24] = replyDraft;
    $[25] = replyError;
    $[26] = setReplyDraft;
    $[27] = thread.messages;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  let t8;
  if ($[29] !== t3 || $[30] !== t4 || $[31] !== t5 || $[32] !== t7) {
    t8 = <div className="flex h-full min-h-0 flex-col">{t3}{t4}{t5}{t6}{t7}</div>;
    $[29] = t3;
    $[30] = t4;
    $[31] = t5;
    $[32] = t7;
    $[33] = t8;
  } else {
    t8 = $[33];
  }
  return t8;
}